/* TPSN — theme behaviour. Vanilla, no framework. Ports the DC interactions:
   1) dark-section header inversion, 2) phone menu + cart drawer,
   3) AJAX add-to-cart, 4) AJAX line quantity / remove. */
/* One rAF-batched scroll tick for the whole theme.

   Every scroll-driven read in this file measures the DOM (getBoundingClientRect),
   which forces a synchronous layout. Registered as raw scroll listeners they ran
   once PER EVENT, per listener — and the smooth-scroll loop below calls
   window.scrollTo on every animation frame, which fires a scroll event, so the
   whole set ran at frame rate on top of the wheel handler itself. Measured on the
   home page: 0.78ms for the header probe plus 0.69ms for the theme-color probe,
   every time.

   Coalescing them means at most one pass per frame no matter how many scroll
   events the browser emits. Reads stay in the same order, so behaviour is
   unchanged; only the frequency drops. Register with tpsnScroll.add(fn). */
var tpsnScroll = (function () {
  var jobs = [], queued = false;
  function run() { queued = false; for (var i = 0; i < jobs.length; i++) jobs[i](); }
  function ping() { if (!queued) { queued = true; requestAnimationFrame(run); } }
  /* A requested frame never runs while the document is hidden, which would leave
     `queued` latched true and silently stall every later update. Browsers do flush
     the pending callback on becoming visible, but clearing the flag here makes
     that guarantee ours rather than the browser's, and re-runs the probes once so
     the bar and the toolbar tint are correct for wherever the page was left. */
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) { queued = false; ping(); }
  });
  return { add: function (fn) { jobs.push(fn); }, ping: ping };
})();
window.addEventListener('scroll', tpsnScroll.ping, { passive: true });
window.addEventListener('resize', tpsnScroll.ping);

(function () {
  'use strict';

  var body = document.body;
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ------------------------------------------------ smooth wheel scrolling ---
     Lenis (lenis.dev, MIT), vendored at assets/lenis.js and loaded before this
     file. Replaced the theme's own ~115-line wheel engine on 2026-09-15 (Leroy).

     ⚠️ THIS IS THE THEME'S ONLY RUNTIME DEPENDENCY. It shipped zero until now —
     the same ground React was rejected on in September. Vendored rather than
     pulled from a CDN so the storefront never depends on a third party being up,
     and so it is versioned in git with the rest of the theme. Upgrading means
     replacing assets/lenis.js deliberately; nothing auto-updates.

     Gated exactly as the old engine was, and for the same reasons:
       - `pointer:fine` only. Touch scrolling is already inertial and runs on the
         compositor thread; re-implementing it in JS is slower during fast flings
         and breaks the mobile URL bar's collapse-on-scroll. Lenis would also keep
         a rAF loop alive on a phone for nothing.
       - Reduced motion opts out entirely, so Lenis never initialises.
     Neither is an oversight. Removing either one undoes a measured decision.

     lerp 0.18 is the old SMOOTH_EASE, carried across unchanged: both are the
     fraction of the remaining distance covered per 60fps frame, and Lenis damps
     it frame-rate independently just as the old loop did. Higher = snappier.

     It drives the real window scroll, not a transform, which is what keeps the
     rest of the theme working untouched: the pinned home hero and the sticky PDP
     and cart columns are position:sticky, the reveals are IntersectionObserver,
     and tpsnScroll listens for real scroll events. */
  var lenis = null;

  function smoothScrollInit() {
    if (!window.matchMedia || typeof window.Lenis !== 'function') return;
    if (!matchMedia('(pointer:fine)').matches) return;
    if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    lenis = new window.Lenis({
      lerp: 0.18,
      smoothWheel: true,
      /* Touch stays native — see the gating note above. */
      syncTouch: false,
      /* ⚠️ Lenis runs its own rAF loop. If anything ever takes over the loop
         (GSAP's ticker was here until 15 Sept, removed unused), autoRaf must go
         false only while that driver is guaranteed present — with nothing
         calling lenis.raf() the wheel is preventDefault-ed and never moves the
         page. */
      autoRaf: true,
      /* ⚠️ The nested-scroller list, and the one place this theme has already
         been bitten. Returning true hands the wheel to the browser so an element
         with its own overflow scrolls itself instead of the page: the predictive
         search results (header AND phone sheet), the mini-cart list, the cart
         drawer body, and the sticky cart summary.

         ⚠️ .revs__track IS DELIBERATELY ABSENT. It is overflow-x:auto with
         overflow-y:hidden, and a browser will happily scroll such a row
         SIDEWAYS in response to a vertical wheel. The old engine's first
         axis-blind version did exactly that: scrolling down over the
         testimonials slid them past while the page appeared to seize. Leaving it
         out means Lenis takes the wheel and preventDefaults, which is what stops
         the browser making that translation. Do not "fix" this by adding it. */
      prevent: function (node) {
        return !!(node && node.closest && node.closest(
          '[data-lenis-prevent], .srch__list, .minicart__list, .drawer__body, .csum'
        ));
      }
    });
  }
  smoothScrollInit();

  /* The old engine bailed out of its wheel handler while the phone menu or the
     cart drawer was open, so the page could not move behind them. lenis.stop()
     is the same thing said to Lenis. Deliberately NOT paired with Lenis's
     optional .lenis-stopped{overflow:hidden} rule: that would also kill
     scrollbar and keyboard scrolling, which the old engine left alone, and on a
     platform with classic scrollbars it shifts the layout when the bar
     disappears. */
  function lenisLock() {
    if (!lenis) return;
    var open = body.getAttribute('data-menu-open') === 'true' ||
               body.getAttribute('data-cart-open') === 'true';
    if (open) lenis.stop(); else lenis.start();
  }
  new MutationObserver(lenisLock).observe(body, {
    attributes: true, attributeFilter: ['data-menu-open', 'data-cart-open']
  });

  /* ---------------------------------------------- header colour inversion --- */
  var header = document.querySelector('[data-header]');
  var headerPhone = document.querySelector('[data-header-phone]');
  var bars = [header, headerPhone].filter(Boolean);
  var darkEls = [].slice.call(document.querySelectorAll('[data-bar-dark]'));
  var bareEls = [].slice.call(document.querySelectorAll('[data-bar-bare]'));
  var BARE_PROBE = 40; // px from viewport top — see syncHeader

  /* ⚠️ A PINNED ELEMENT CANNOT REPORT WHERE THE PAGE IS. The home hero is sticky
     (the footage holds still while the page wipes up over it), and once it pins,
     both of the numbers you would reach for follow the pin: getBoundingClientRect
     says "top of the viewport" forever, and so does offsetTop — Chrome includes
     the sticky offset in it (measured 2026-09-14: a sticky box in a container
     scrolled 500px reports offsetTop 500, rect.top 0). Reading either one held
     the bare header over every section on the page.
     So measure the element doing the WIPING instead — the section after the hero,
     which is in normal flow and whose top edge is exactly where the bare state
     should end. Resolved once here; the sticky rule is not behind a media query,
     so it cannot change under the page. */
  var bareProbes = bareEls.map(function (el) {
    var wrap = el.parentNode;
    var pinned = wrap && wrap.nodeType === 1 && getComputedStyle(wrap).position === 'sticky';
    return { el: el, wiper: pinned ? wrap.nextElementSibling : null };
  });
  function coversBare(probe) {
    for (var i = 0; i < bareProbes.length; i++) {
      var p = bareProbes[i], r;
      if (p.wiper) {
        if (p.wiper.getBoundingClientRect().top > probe) return true;
      } else {
        r = p.el.getBoundingClientRect();
        if (r.top <= probe && r.bottom >= probe) return true;
      }
    }
    return false;
  }

  // is any of these sections under the bar's lower edge right now?
  function covers(els, probe) {
    for (var i = 0; i < els.length; i++) {
      var r = els[i].getBoundingClientRect();
      if (r.top <= probe && r.bottom >= probe) return true;
    }
    return false;
  }

  function syncHeader() {
    if (!bars.length) return;
    // Two different probe lines, because the two states want opposite timing:
    //
    // dark  — probe at the bar's own bottom edge, so the bar inverts as soon as a
    //         dark section reaches it rather than after it's fully underneath.
    // bare  — probe near the top of the viewport, so the glass panel stays off
    //         until the video has all but scrolled away. Keying this to the bar's
    //         bottom edge brought the panel back while the last ~70px of footage
    //         was still visible above it; 0 (fully gone) felt a beat too late.
    //         Raise BARE_PROBE to bring the panel back earlier, lower it for later.
    var probeDark = 0;
    for (var i = 0; i < bars.length; i++) probeDark = Math.max(probeDark, bars[i].getBoundingClientRect().bottom);
    if (!probeDark) probeDark = 30;
    var dark = covers(darkEls, probeDark);
    var bare = coversBare(BARE_PROBE);
    for (var j = 0; j < bars.length; j++) {
      bars[j].classList.toggle('is-dark', dark || bare);
      bars[j].classList.toggle('is-bare', bare);
    }
  }
  tpsnScroll.add(syncHeader);
  syncHeader();


  /* ------------------------------------------------------ menu + drawer --- */
  function setState(key, val) {
    body.setAttribute(key, val ? 'true' : 'false');
    body.style.overflow = (body.getAttribute('data-menu-open') === 'true' || body.getAttribute('data-cart-open') === 'true') ? 'hidden' : '';
  }
  function toggle(key) { setState(key, body.getAttribute(key) !== 'true'); }

  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-action]');
    if (!t) return;
    var a = t.getAttribute('data-action');
    if (a === 'toggle-menu') { setState('data-cart-open', false); setState('data-search-open', false); toggle('data-menu-open'); }
    if (a === 'toggle-cart') { setState('data-menu-open', false); setState('data-search-open', false); toggle('data-cart-open'); }
    /* Phone search (2026-09-17). Desktop's panel is hover/:focus-within and needs
       no state at all; a phone has neither, so the same panel is driven by an
       attribute here. The three are mutually exclusive for the same reason the
       first two already were — they all hang off the same 54px bar.
       ⚠ No scroll lock, unlike menu and cart: this is a dropdown anchored to the
       bar, not a full-height surface, and locking the body under a panel the
       shopper is typing into fights the keyboard on iOS. setState only locks on
       menu/cart, so search is correct by omission — do not add it to that list. */
    if (a === 'toggle-search') {
      setState('data-menu-open', false); setState('data-cart-open', false);
      toggle('data-search-open');
      if (body.getAttribute('data-search-open') === 'true') {
        var f = document.querySelector('.hdr--phone [data-search-input]');
        /* Focus after the panel is visible; focusing a visibility:hidden field
           is a no-op and iOS then refuses to raise the keyboard on the tap that
           follows. One frame is enough — the transition can still be running. */
        if (f) requestAnimationFrame(function () { f.focus(); });
      }
    }
    if (a === 'close') { setState('data-menu-open', false); setState('data-cart-open', false); setState('data-search-open', false); }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') { setState('data-menu-open', false); setState('data-cart-open', false); setState('data-search-open', false); }
  });

  /* -------------------------------------------------------------- cart --- */
  function fetchDoc(url) {
    return fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.text(); })
      .then(function (html) { return new DOMParser().parseFromString(html, 'text/html'); });
  }

  function swap(doc, selector) {
    var next = doc.querySelector(selector), cur = document.querySelector(selector);
    if (next && cur) cur.innerHTML = next.innerHTML;
  }

  /* Pulls fresh server-rendered markup for every place the cart is shown:
     the drawer, the header hover panel and the cart page. */
  function refreshCart() {
    var jobs = [
      fetchDoc(root + '?section_id=cart-drawer').then(function (d) { swap(d, '[data-cart-drawer]'); })
    ];
    if (document.querySelector('.minicart__panel')) {
      jobs.push(fetchDoc(root + '?section_id=header').then(function (d) { swap(d, '.minicart__panel'); }));
    }
    if (document.querySelector('.cartpage')) {
      jobs.push(fetchDoc(location.pathname + location.search).then(function (d) { swap(d, '.cartpage'); }));
    }
    /* cart.js used to be chained AFTER the section renders, which made every cart
       interaction three SERIAL round trips. Measured from the click on 2026-09-08:
       cart/add.js 1064ms -> the two section renders 653ms -> cart.js 822ms, i.e.
       ~2.5s before the numbers settled, and the same shape (~2.6s) for a quantity
       change. cart.js does not depend on the rendered sections in any way, so it
       runs alongside them now and the chain is two waves instead of three. */
    var cartJson = fetch(root + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
    return Promise.all([Promise.all(jobs), cartJson])
      .then(function (settled) {
        var cart = settled[1];
        /* <body data-cart-count> (theme.liquid) is a STATE hook, not a display
           node — it matches this selector, so writing textContent into it
           replaced the entire document with the bare cart number after every
           add/remove. Keep the attribute in sync; only write text into spans. */
        [].slice.call(document.querySelectorAll('[data-cart-count],[data-minicart-count]'))
          .forEach(function (el) {
            if (el === document.body) { el.setAttribute('data-cart-count', cart.item_count); return; }
            el.textContent = cart.item_count;
          });
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        return cart;
      });
  }

  function notify(msg) {
    var els = [].slice.call(document.querySelectorAll('[data-cart-error]'));
    if (!els.length) return;
    els.forEach(function (el) { el.textContent = msg; el.hidden = false; });
    setTimeout(function () { els.forEach(function (el) { el.hidden = true; }); }, 6000);
  }

  function errorFrom(data, fallback) {
    if (!data) return fallback;
    return data.description || data.message || fallback;
  }

  /* line quantity + remove — delegated so it survives every re-render */
  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-cart-change]');
    if (!t || t.disabled) return;
    e.preventDefault();
    t.disabled = true;
    body.setAttribute('data-cart-busy', 'true');
    fetch(root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ line: Number(t.getAttribute('data-line')), quantity: Number(t.getAttribute('data-to')) })
    })
      .then(function (r) {
        return r.json().then(function (d) {
          if (!r.ok) throw new Error(errorFrom(d, 'Could not update your cart.'));
          return refreshCart();
        });
      })
      .catch(function (err) {
        /* On success refreshCart() re-renders these controls, so `t` is discarded
           and its disabled flag goes with it. On failure nothing re-renders —
           without this the button stays disabled and the row can never be retried. */
        t.disabled = false;
        notify(err.message || 'Could not update your cart.');
      })
      .then(function () { body.removeAttribute('data-cart-busy'); });
  });

  /* --------------------------------------------------- AJAX add to cart --- */
  var peekTimer = null;
  /* how long the desktop dropdown stays open after an add before auto-closing.
     Hovering it holds it open independently (the :hover rule), so this is only
     the hands-off duration. */
  var PEEK_MS = 10000;
  /* matches the breakpoint where .hdr--phone replaces .hdr--desktop */
  function isPhone() { return window.matchMedia('(max-width:959px)').matches; }
  /* Notify me — open the capture field in place of the pill. Delegated so it
     survives the product form being re-rendered, and a no-op when the notify
     form is not on the page. No JS: the button does nothing and the shopper
     sees the sold-out state, which is what they saw before this existed. */
  document.addEventListener('click', function (e) {
    var hit = e.target && e.target.closest ? e.target.closest('[data-notify-open]') : null;
    if (!hit) return;
    var nf = hit.closest('[data-notify-form]');
    if (!nf) return;
    nf.setAttribute('data-open', '');
    var field = nf.querySelector('#notify-email');
    if (field) field.focus();
  });

  /* A failed submit comes back as a fresh page render, so the panel would be
     collapsed again with the error hidden inside it. Reopen it when Liquid has
     printed one, rather than driving it from a form-tag attribute whose falsy
     rendering is not guaranteed. */
  (function () {
    var nf = document.querySelector('[data-notify-form]');
    if (nf && nf.querySelector('.notice')) nf.setAttribute('data-open', '');
  })();

  var form = document.querySelector('[data-product-form]');
  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var btn = form.querySelector('[data-atc]');
      var label = btn && btn.querySelector('span');
      var was = label ? label.textContent : '';
      var err = form.querySelector('[data-cart-error]');
      if (err) { err.hidden = true; }
      if (btn) btn.disabled = true;

      fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Accept': 'application/json' }, body: new FormData(form) })
        .then(function (r) {
          return r.json().then(function (d) {
            /* 422 = sold out / not enough stock. Never treat it as a success. */
            if (!r.ok) throw new Error(errorFrom(d, 'This item could not be added to your cart.'));
            return d;
          });
        })
        .then(function () {
          if (label) label.textContent = 'Added to Cart \u2713';
          /* Open this viewport's cart surface immediately \u2014 doing it after
             refreshCart() delayed the feedback by ~2.5s of round-trips.
             One surface per viewport, as in the design comp: the slide-in
             drawer is the PHONE cart, the header dropdown is the desktop one.
             `data-cart-open` only drives .drawer, so firing it on desktop
             opened the phone drawer on top of the page. */
          clearTimeout(peekTimer);
          if (isPhone()) setState('data-cart-open', true);
          else document.body.setAttribute('data-minicart-open', 'true');
          return refreshCart();
        })
        .then(function () {
          /* Auto-close the desktop peek only once the contents are fresh, so it
             can't shut mid-refresh. The phone drawer stays open until
             dismissed, as before. */
          if (!isPhone()) {
            peekTimer = setTimeout(function () {
              document.body.removeAttribute('data-minicart-open');
            }, PEEK_MS);
          }
          setTimeout(function () { if (label && was) label.textContent = was; }, 1800);
        })
        .catch(function (error) {
          if (label && was) label.textContent = was;
          /* the surface is opened before refreshCart now, so a failure mid-flight
             would otherwise leave the dropdown hanging open over the page */
          clearTimeout(peekTimer);
          document.body.removeAttribute('data-minicart-open');
          notify(error.message || 'This item could not be added to your cart.');
        })
        .then(function () { if (btn) btn.disabled = false; });
    });

    /* The PDP section, not the form. The option radios and — since the quantity
       moved up into the specs row — the quantity stepper both live OUTSIDE the
       {%- form -%}, which wraps only the colour row, the price and the button.
       Listening on the form heard neither. */
    var scope = form.closest('.pdp') || document;

    /* Quantity steppers. The <input name="quantity"> posts on its own through its
       form="pdp-form" owner attribute, so this only drives the +/- buttons and the
       floor — a no-JS shopper can still type a number and add it. Clamped at 1
       because cart/add.js rejects 0 with a 422 and the shopper would get an error
       where they expected a removal. */
    scope.addEventListener('click', function (e) {
      var step = e.target.closest && e.target.closest('[data-qty-step]');
      if (!step) return;
      var input = scope.querySelector('[data-qty-input]');
      if (!input) return;
      var next = (parseInt(input.value, 10) || 1) + parseInt(step.getAttribute('data-qty-step'), 10);
      input.value = next < 1 ? 1 : next;
    });

    /* Typing straight into the field can leave it blank, 0 or negative on the way to a
       real number; normalise on the way out rather than fighting the caret. */
    scope.addEventListener('change', function (e) {
      if (!e.target.matches || !e.target.matches('[data-qty-input]')) return;
      var n = parseInt(e.target.value, 10);
      e.target.value = (!n || n < 1) ? 1 : n;
    });

    /* Variant picker: pills write into the hidden variant id.

       Scope is the PDP section, NOT the form. The option radios live in
       .pdp__mid, which is a SIBLING of the {% form %} — the form only wraps the
       price and the button. Querying and listening on the form therefore matched
       nothing and heard nothing, so choosing a colour silently did nothing at
       all: no variant swap, no price change, and a sold-out colour still showed
       an enabled "Add to Cart". That was invisible for as long as every product
       had a single default variant. Keep this scoped to .pdp — moving the pills
       inside the form instead would break the two-column layout, which relies on
       .pdp__mid and .atc being separate flex children. */
    var picker = form.querySelector('[data-variant-id]');
    /* In the h1, which sits in .pdp__title — outside the form, inside .pdp. Same
       reason everything else here is scoped to .pdp and not to the form. */
    var shapeNameEl = scope.querySelector('[data-shape-name]');
    var variants = null;
    try { variants = JSON.parse(document.getElementById('tpsn-variants').textContent); } catch (e) {}

    /* Gallery follows the colour.

       Picking a colour used to change the price, the button and the URL and leave
       the photographs alone, so a shopper choosing Teal went on looking at the
       White paddle. Variant images are assigned in admin; main-product.liquid
       stamps each .gallery__item with its media id and marks the ones that belong
       to a variant, so this only has to reorder and hide.

       A variant's image leads a group: the plain images after it in media order
       carry data-group="<its id>" and travel with it (main-product.liquid explains
       the convention). Picking a variant shows its group first and hides every
       other group; shared images, the ones before the first variant image, stay.

       ⚠️ It must never empty the gallery — the same rule the search prune follows
       for the same reason. A variant with no image of its own opens the first
       group (the photograph the card shows) rather than a blank column or every
       colour at once. A product with no variant images at all is left alone. */
    var gallery = scope.querySelector('.gallery');

    /* Spec table follows the variant.

       A model sold in two shapes has two geometries, and .specs is rendered from
       a single metafield — which is the whole reason the Quanta Pro R3 and R4
       were originally two separate products. Now that they are one, each variant
       carries its own specs and the table is rebuilt on change. Variants with no
       specs of their own leave the product-level table alone rather than blanking
       it, so a product where only some variants differ still reads correctly. */
    var specsEl = scope.querySelector('.specs');

    function renderSpecs(rows) {
      if (!specsEl || !rows || !rows.length) return;
      var frag = document.createDocumentFragment();
      rows.forEach(function (row) {
        var parts = String(row).split('|');
        var dt = document.createElement('dt');
        var dd = document.createElement('dd');
        /* textContent, not innerHTML: these strings come from a metafield a
           merchant edits, and a spec label is never markup. */
        dt.textContent = parts.shift() || '';
        dd.textContent = parts.join('|');
        frag.appendChild(dt);
        frag.appendChild(dd);
      });
      specsEl.innerHTML = '';
      specsEl.appendChild(frag);
    }

    /* The item that leads the group a variant opens on: its own image, else the
       first variant image (see the note above). Null on a product with no
       variant images, which leaves the gallery alone. */
    function galleryTarget(match) {
      if (!gallery) return null;
      var t = (match && match.image) ?
        gallery.querySelector('.gallery__item[data-media-id="' + match.image + '"]') : null;
      return t || gallery.querySelector('.gallery__item[data-variant-image]');
    }

    /* Phone carousel dashes (2026-09-16). On phones .gallery is a horizontal
       scroll-snap row; one dash per VISIBLE item, in visual order (the open group
       carries order:-1), active dash from an IntersectionObserver on the items —
       no scroll listener. On desktop .gallery__dots is display:none and the
       gallery doesn't scroll, so all of this is inert there. */
    var dotsEl = scope.querySelector('.gallery__dots');
    var dotsIO = null;
    function paintDots() {
      if (!gallery || !dotsEl) return;
      if (gallery.scrollLeft) gallery.scrollLeft = 0;
      var items = [].slice.call(gallery.querySelectorAll('.gallery__item')).filter(function (el) { return !el.hidden; });
      items.sort(function (a, b) { return (parseInt(a.style.order || 0, 10) - parseInt(b.style.order || 0, 10)); });
      dotsEl.innerHTML = '';
      dotsEl.hidden = items.length < 2;
      var dots = items.map(function (el, i) {
        var d = document.createElement('span');
        if (i === 0) d.className = 'is-on';
        dotsEl.appendChild(d);
        return d;
      });
      if (dotsIO) dotsIO.disconnect();
      if (!('IntersectionObserver' in window) || items.length < 2) return;
      dotsIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.intersectionRatio < 0.6) return;
          var i = items.indexOf(e.target);
          dots.forEach(function (d, k) { d.className = k === i ? 'is-on' : ''; });
        });
      }, { root: gallery, threshold: [0.6] });
      items.forEach(function (el) { dotsIO.observe(el); });
    }

    function syncGallery(match) {
      syncGalleryItems(match);
      placeFeatures();
      paintDots();
    }

    /* Features after the FIRST photograph, desktop only (Leroy, 2026-09-21:
       "picture 1 product info then picture 2 and 3").

       Why this is a DOM move and not `order`, which is what everything else in
       this gallery uses: the block has to land BETWEEN two children of
       .gallery, so it has to be a child of .gallery. The obvious alternative --
       display:contents on .gallery so its items and .features share one flex
       context -- is not available: `.pdp .gallery` carries
       view-transition-name:tpsn-pdp-media for the card->PDP morph, and an
       element with display:contents generates no box, so naming it does
       nothing. Keeping .gallery a real box is what keeps that morph alive.

       ⚠ It must be undone below 768. .pdp__media is display:contents on phones,
       which promotes .features into .pdp__grid where it is placed explicitly at
       grid-row 5 -- inside .gallery it would instead become a slide in the
       horizontal scroll-snap carousel. Hence the matchMedia guard and the move
       back to featuresHome.

       ⚠ The renumber is the point. Visible items carry order -1 (an open
       variant group) or 0, so there is no value that sits between the first and
       second -- "after the first image" is not expressible in the -1/0 scheme.
       Re-indexing the visible items 0, 2, 3, 4 ... frees 1 for the block. It
       runs AFTER syncGalleryItems, which resets and re-applies -1, and BEFORE
       paintDots, which sorts on the same values and is unaffected either way
       because the visual order is identical.

       ⚠ Fewer than two visible images means there is no "after the first", so
       the block stays where Liquid put it. */
    var featuresEl = scope.querySelector('.features');
    var featuresHome = featuresEl && featuresEl.parentNode;
    var featuresWide = window.matchMedia('(min-width:768px)');

    function placeFeatures() {
      if (!featuresEl || !gallery) return;

      var visible = [].slice.call(gallery.querySelectorAll('.gallery__item'))
        .filter(function (el) { return !el.hidden; })
        .sort(function (a, b) {
          return (parseInt(a.style.order || 0, 10) - parseInt(b.style.order || 0, 10));
        });

      if (!featuresWide.matches || visible.length < 2) {
        if (featuresEl.parentNode !== featuresHome) featuresHome.appendChild(featuresEl);
        featuresEl.style.order = '';
        return;
      }

      if (featuresEl.parentNode !== gallery) gallery.appendChild(featuresEl);
      visible.forEach(function (el, i) { el.style.order = String(i === 0 ? 0 : i + 1); });
      featuresEl.style.order = '1';
    }

    /* Only the placement is breakpoint-dependent, so a width change re-runs
       placeFeatures alone -- re-syncing the variant here would need a current
       match this scope does not keep, and nothing about the variant changed. */
    if (featuresWide.addEventListener) {
      featuresWide.addEventListener('change', placeFeatures);
    }

    function syncGalleryItems(match) {
      if (!gallery) return;
      var items = gallery.querySelectorAll('.gallery__item[data-media-id]');
      if (!items.length) return;

      var target = galleryTarget(match);

      [].forEach.call(items, function (el) {
        el.style.order = '';
        el.hidden = false;
      });
      if (!target) return;

      /* order, not DOM moves: the images keep their document position (so lazy
         loading and the alt text order stay put) and the compositor does the work.
         The whole group shares order -1, so it keeps its own front-then-angle order. */
      var group = target.getAttribute('data-media-id');
      [].forEach.call(items, function (el) {
        if (el === target || el.getAttribute('data-group') === group) el.style.order = '-1';
        else if (el.hasAttribute('data-variant-image') || el.hasAttribute('data-group')) el.hidden = true;
      });
    }

    /* Run once on load so a ?variant= link opens on the right photograph, not just
       the right price. */
    if (variants && picker) {
      var initial = parseInt(picker.value, 10);
      syncGallery(variants.filter(function (v) { return v.id === initial; })[0]);
      syncOptionGroups(scope, variants);
    } else {
      paintDots();
    }

    /* An option group is only a CHOICE if, given what is picked in the OTHER
       groups, more than one of its values exists as a variant. The V-Sol Tour is
       the case that forced this: Vatic sells the hybrid in a long and a short
       handle and the elongated in one handle only, so on Elongated the Handle row
       offered a "Short" that does not exist. Marking it sold out was worse than
       useless — a strike reads as "out of stock", i.e. "ask me when it's back",
       when the honest meaning is "this combination is not made".

       ⚠️ Gated on group.values.length > 1, so a genuinely single-value option is
       NEVER hidden. Colour on the V-Sol is one value, Black, and it is there to
       state a fact rather than offer a choice — hiding it would silently undo a
       deliberate merchandising decision. Conditional, not merely singular, is
       what earns the hide. */
    function syncOptionGroups(scope, variants) {
      var groups = [].slice.call(scope.querySelectorAll('.picks__group'));
      groups.forEach(function (g, idx) {
        var input = g.querySelector('input[data-option]');
        if (!input) return;
        var pos = input.getAttribute('data-option');
        var total = g.querySelectorAll('input[data-option]').length;
        /* ⚠️ CASCADE, NOT A SYMMETRIC FILTER. A group is constrained only by the
           groups ABOVE it in the canonical order (Shape, then Handle, then
           Colour), never by the ones below. The symmetric version was written
           first and was wrong in a way worth recording: picking Handle "Short"
           left Hybrid as the only shape with a Short variant, so it hid the SHAPE
           row — removing the primary control and stranding the shopper with no way
           back to Elongated. Earlier choices narrow later ones; later choices
           never erase earlier ones. */
        var constrain = {};
        groups.slice(0, idx).forEach(function (pg) {
          var pin = pg.querySelector('input[data-option]:checked');
          if (pin) constrain[pin.getAttribute('data-option')] = pin.value;
        });
        var seen = {};
        variants.forEach(function (v) {
          for (var k in constrain) {
            if (v.options[k - 1] !== constrain[k]) return;
          }
          seen[v.options[pos - 1]] = true;
        });
        g.hidden = total > 1 && Object.keys(seen).length <= 1;
      });
    }

    /* Variant swaps crossfade: the old photographs, spec table, heading, price
       and button label dissolve straight into the new ones (Leroy, 2026-09-11 —
       first "feels kind of choppy", then "don't fade in or out, fade between").
       A dip to blank and back was the first attempt; it read as a blink. Only
       what actually differs takes part: a shape change that keeps the same
       photographs leaves the gallery alone.

       It is a same-document View Transition. The changing parts get a
       view-transition-name only for the length of the swap: a name left on
       permanently would also apply to the cross-document page transitions
       (@view-transition in tpsn.css) and morph one product's gallery into the
       next product's. The header is never named — see the wordmark note there.
       Durations and the pointer-events pass-through live in tpsn.css under
       "Variant crossfade".

       The new photographs start loading at the tap and get up to 300ms to decode
       inside the update callback, so the crossfade lands on the picture rather
       than on an empty panel. A second tap mid-swap starts a new transition (the
       browser skips the old one) and every callback applies the latest variant,
       so the page always ends on the last tap.

       No startViewTransition (Safari before 18), reduced motion, or nothing
       visible changing: the swap is instant. The hidden input, the URL and the
       button's disabled state never wait for any of this — a tap on Add to Cart
       mid-fade must buy what is selected. */
    var atcEl = form.querySelector('[data-atc]');
    var atcText = atcEl && atcEl.querySelector('span');
    var priceEl = document.querySelector('[data-price-amount]');
    /* There are TWO price elements now — one in the product form, one in the
       notify form that replaces it when the variant is sold out. Only one is
       ever displayed, but both must track the variant or the visible price goes
       stale the moment the hidden one is the one being written. priceEl stays
       the first for the view transition, whose name on a display:none element
       is inert anyway. */
    var priceEls = [].slice.call(document.querySelectorAll('[data-price-amount]'));
    var calm = !!(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches);
    var shown = (variants && picker) ?
      variants.filter(function (v) { return v.id === parseInt(picker.value, 10); })[0] : null;
    var titleEl = shapeNameEl && shapeNameEl.closest('h1');
    var pending = null, swapGen = 0;
    var DECODE_CAP = 300;   /* ms */
    /* The h1 rather than the model-name span inside it: a named inline box that
       wraps across two lines can't be captured, and the whole transition would be
       skipped. The price and button label are single-line inline boxes. */
    var VT_NAMES = [[gallery, 'tpsn-gallery'], [specsEl, 'tpsn-specs'], [titleEl, 'tpsn-title'],
                    [priceEl, 'tpsn-price'], [atcText, 'tpsn-atc']];

    /* Both labels come from the button itself, not from literals here: on a
       pre-order product the in-stock wording reads "Pre-order", and hardcoding
       "Add to Cart" turned it back into a normal purchase as soon as the shopper
       picked a different colour. The sold-out wording had exactly that bug in
       waiting — it was the literal "Sold Out" — so it now reads
       data-atc-oos-label the same way. The fallbacks are last resorts only. */
    function atcWording(v) {
      if (!v) return '';
      return v.available ? ((atcEl && atcEl.getAttribute('data-atc-label')) || 'Add to Cart')
                         : ((atcEl && atcEl.getAttribute('data-atc-oos-label')) || 'Notify me');
    }
    function groupKey(v) {
      var t = galleryTarget(v);
      return t ? t.getAttribute('data-media-id') : '';
    }
    function groupImages(v) {
      var t = galleryTarget(v);
      if (!t) return [];
      var key = t.getAttribute('data-media-id');
      return [].slice.call(gallery.querySelectorAll('.gallery__item')).filter(function (el) {
        return el === t || el.getAttribute('data-group') === key;
      }).map(function (el) { return el.querySelector('img'); }).filter(Boolean);
    }
    function changing(v) {
      var s = shown || {};
      var out = [];
      if (gallery && groupKey(v) !== groupKey(shown)) out.push(gallery);
      if (specsEl && v.specs && v.specs.length && JSON.stringify(v.specs) !== JSON.stringify(s.specs)) out.push(specsEl);
      if (titleEl && (v.model || '') !== (s.model || '')) out.push(titleEl);
      if (priceEl && v.priceLabel && v.priceLabel !== s.priceLabel) out.push(priceEl);
      if (atcText && atcWording(v) !== atcWording(shown)) out.push(atcText);
      return out;
    }
    function applyVisual(v) {
      if (atcText) atcText.textContent = atcWording(v);
      if (v.priceLabel) priceEls.forEach(function (el) { el.textContent = v.priceLabel; });
      syncGallery(v);
      renderSpecs(v.specs);
      /* Heading follows the shape's model name: "Quanta Pro" becomes
         "Quanta Pro R3". main-product.liquid resolves it from the Shape
         option's parenthetical and ships one per variant, so this only has to
         write it. Empty string when the shape has no model name, which is why
         the span is always rendered — a product whose first variant has no
         bracket could otherwise never gain the name. The leading space is part
         of the value so an empty one contributes nothing. */
      if (shapeNameEl) shapeNameEl.textContent = v.model ? ' ' + v.model : '';
      shown = v;
    }
    function swapTo(v) {
      var els = changing(v);
      pending = v;
      if (calm || !document.startViewTransition || !els.length) { applyVisual(v); return; }
      groupImages(v).forEach(function (img) { img.loading = 'eager'; });
      var gen = ++swapGen;
      /* The PDP's blocks carry STATIC view-transition-names for the page-to-page
         morph (tpsn.css, "Product page: sequential reveal"). Those names must be
         off for the length of a variant swap or all six blocks would animate on
         every tap, instead of only what differs. The class switches them to
         none; the inline names written just below are inline, so they still win
         on the elements that are genuinely changing. */
      document.documentElement.classList.add('vt-swap');
      /* ⚠ THE GALLERY MUST NOT BE NAMED UNDER THE NAV (2026-09-19). A named
         element is lifted into the ::view-transition top layer, which the
         header's z-index:100 cannot reach, so a gallery that overlaps the
         sticky bar paints over it for the length of the swap -- Leroy: "the
         photo jumps in front of the nav bar then hides again". Naming the
         header as well was tried and reverted the same night: a snapshot
         cannot sample a live backdrop, so the pill lost its blur and flashed
         flat white instead (see the note in tpsn.css).
         Left unnamed, the gallery stays in the ROOT snapshot -- the same one
         the nav is in -- so the two cross-fade together, in the right order,
         on the root's own .22s. The morph is only given up while the column
         is actually under the bar; at the top of the page, which is where a
         shopper usually switches colour, it still gets its own group. */
      var navEl = document.querySelector('[data-header]');
      var navBottom = navEl ? navEl.getBoundingClientRect().bottom : 0;
      var galleryUnderNav = !!gallery && gallery.getBoundingClientRect().top < navBottom;
      VT_NAMES.forEach(function (p) {
        if (!p[0] || els.indexOf(p[0]) < 0) return;
        if (p[0] === gallery && galleryUnderNav) return;
        p[0].style.viewTransitionName = p[1];
      });
      var t = document.startViewTransition(function () {
        var decoded = Promise.all(groupImages(pending).map(function (img) {
          return img.decode ? img.decode().catch(function () {}) : null;
        }));
        var cap = new Promise(function (r) { setTimeout(r, DECODE_CAP); });
        return Promise.race([decoded, cap]).then(function () { applyVisual(pending); });
      });
      /* Names come off when the swap is over, but only by the latest swap, so an
         earlier one finishing late can't strip names from a transition still
         running. */
      function clearNames() {
        if (gen !== swapGen) return;
        VT_NAMES.forEach(function (p) { if (p[0]) p[0].style.viewTransitionName = ''; });
        document.documentElement.classList.remove('vt-swap');
      }
      t.finished.then(clearNames, clearNames);
    }

    scope.addEventListener('change', function (e) {
      if (!variants || !picker || !e.target.hasAttribute('data-option')) return;
      /* Sorted by data-option, NOT left in DOM order. querySelectorAll returns
         document order, and the match below joins these against v.options, which
         is always in Shopify's option-position order. The two agreed only by
         accident until the pickers were reordered for display (Shape first,
         2026-09-09) — after which every join missed and no variant would ever
         have matched, silently. Sorting here makes the markup order irrelevant. */
      var checked = [].slice.call(scope.querySelectorAll('input[data-option]:checked'))
        .sort(function (a, b) {
          return a.getAttribute('data-option') - b.getAttribute('data-option');
        });
      /* The combination the shopper just built may not exist at all now that
         impossible variants are deleted rather than listed as sold out: coming
         from Hybrid/Short and picking Elongated asks for a variant that was never
         made. Rather than leave the page inert — the old behaviour, where nothing
         updated and the button kept the previous variant's state — honour the
         control they actually touched and move the others to the nearest variant
         that has it, preferring one in stock. */
      var want = checked.map(function (i) { return i.value; });
      if (!variants.filter(function (v) { return v.options.join('~') === want.join('~'); })[0]) {
        var pos = e.target.getAttribute('data-option');
        var cands = variants.filter(function (v) { return v.options[pos - 1] === e.target.value; });
        var fallback = cands.filter(function (v) { return v.available; })[0] || cands[0];
        if (fallback) {
          checked.forEach(function (i) {
            var want2 = fallback.options[i.getAttribute('data-option') - 1];
            if (i.value !== want2) {
              var alt = scope.querySelector('input[data-option="' + i.getAttribute('data-option') + '"][value="' + window.CSS.escape(want2) + '"]');
              if (alt) alt.checked = true;
            }
          });
          checked = [].slice.call(scope.querySelectorAll('input[data-option]:checked'))
            .sort(function (a, b) {
              return a.getAttribute('data-option') - b.getAttribute('data-option');
            });
        }
      }
      syncOptionGroups(scope, variants);
      /* Colour swatches have no visible text, so the option label names the
         selected value. Updated before the variant lookup on purpose: the name
         should follow the tap even for a combination that has no variant. */
      checked.forEach(function (i) {
        var out = scope.querySelector('[data-option-value="' + i.getAttribute('data-option') + '"]');
        if (out) out.textContent = i.value;
      });
      var chosen = checked.map(function (i) { return i.value; });
      var match = variants.filter(function (v) { return v.options.join('~') === chosen.join('~'); })[0];
      if (match) {
        picker.value = match.id;
        /* Reflect the choice in the URL so a colour can be linked, shared and
           reloaded. Picking a variant used to leave location.search empty, so
           every colour of a product was the same URL and there was no way to
           send someone the one you meant. replaceState, not pushState: tapping
           through six swatches should not bury the previous page under six
           history entries. */
        if (window.history && history.replaceState) {
          try {
            var u = new URL(window.location.href);
            u.searchParams.set('variant', match.id);
            history.replaceState({}, '', u.pathname + u.search + u.hash);
          } catch (e) {}
        }
        /* Swap the whole buy block. data-oos on .pdp decides which of the two
           forms is displayed; CSS does the rest. Mirrors what Liquid renders on
           load — change both or a variant switch disagrees with a page load. */
        var pdpEl = atcEl && atcEl.closest ? atcEl.closest('.pdp') : null;
        if (pdpEl) pdpEl.setAttribute('data-oos', match.available ? 'false' : 'true');

        /* The waitlist key. Product tag so one send covers a restock across
           colours; variant tag so the reorder signal says WHICH one people
           wanted. Rebuilt here because the hidden input was rendered for
           whichever variant the page opened on. */
        var tagsEl = document.querySelector('[data-notify-tags]');
        if (tagsEl) {
          var base = tagsEl.getAttribute('data-notify-base') || '';
          if (!base) {
            base = (tagsEl.value.split(',')[0] || '').trim();
            tagsEl.setAttribute('data-notify-base', base);
          }
          var tags = [base];
          if (match.sku) tags.push('bis-' + match.sku);
          /* marketing-optin is unconditional since 2026-09-17 — the consent
             checkbox it used to read is gone and every signup is a newsletter
             signup. Rebuilt here rather than trusted from the rendered value
             because this whole block re-derives the tag list from scratch on
             every variant change; dropping it would strip the tag on any
             signup that switched variant first. */
          tags.push('marketing-optin');
          tagsEl.value = tags.filter(Boolean).join(',');
        }

        /* Come back to the variant that was signed up for. Shopify redirects a
           posted customer form with ?customer_posted=true and nothing else, so
           without this the page reopens on the first BUYABLE variant — and the
           confirmation is inside the notify form, which only shows while
           data-oos is true. The message was there; the variant it belonged to
           was not. Rendered by Liquid for whichever variant the page opened on,
           same as the tags above, so it has to be rewritten here too. */
        var rtEl = document.querySelector('[data-notify-form] input[name="return_to"]');
        if (rtEl) {
          /* customer_posted=true is NOT added for us. Shopify appends it on the
             default redirect, but a return_to is taken literally, and that
             parameter is the ONLY thing form.posted_successfully? reads. Drop it
             and the shopper lands on the right variant looking at the Notify me
             pill again, with no sign the signup worked. Keep in step with the
             Liquid capture that renders this input. */
          rtEl.value = window.location.pathname + '?variant=' + match.id + '&customer_posted=true';
        }

        /* Belt and braces: if the CSS toggle above ever fails, the product form
           is still visible, so its button must not offer to buy a sold-out
           variant. `type` is what keeps the two apart — submit reaches the
           form's submit handler, button does not. */
        if (atcEl) {
          atcEl.disabled = false;
          atcEl.type = match.available ? 'submit' : 'button';
        }
        swapTo(match);
      }
    });
  }
})();


/* The cart summary used to be pinned here: a `fixed` panel whose height was
   locked to the viewport and whose bottom offset was rewritten every scroll
   frame. It is now `position:sticky` in tpsn.css — content-height, handled by
   the compositor, no scroll listener. Deleted deliberately; don't reintroduce. */

/* Predictive search — live preview in the header dropdown.

   Results are rendered by sections/predictive-search.liquid through the Section
   Rendering API rather than templated in JS, so the row markup lives in Liquid
   next to the mini-cart rows it borrows from and can never drift from them.

   Opening and closing is pure CSS (:hover / :focus-within on .hdr__srch) so the
   panel behaves like the cart and Shop menus and needs no state here. The Search
   link keeps its href and its click, so clicking through to the full /search page
   still works. This module only turns typing into results. */
(function () {
  var wraps = document.querySelectorAll('[data-search]');
  if (!wraps.length) return;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  // One instance since 2026-09-17: the desktop dropdown. The phone sheet had a
  // second one until its field was replaced by a magnifier linking to /search.
  // The loop stays — it costs nothing, it is what let the two coexist (each with
  // its own debounce timer, in-flight request and resting markup), and the phone
  // is the obvious place a second instance comes back.
  [].forEach.call(wraps, function (wrap) {
  var input = wrap.querySelector('[data-search-input]');
  var results = wrap.querySelector('[data-search-results]');
  if (!input || !results) return;

  var DEBOUNCE = 200;   // ms of quiet before asking the server
  var MIN_CHARS = 2;    // one letter matches most of the catalogue — not worth a round trip
  /* 3.5 rows are visible; the rest is what you scroll to. Trimmed 8 -> 6
     because predictive search PADS TOWARD THE LIMIT — it is a typeahead, so it
     keeps filling with progressively weaker matches rather than stopping when
     the good ones run out. Measured on a 12-product catalogue: "paddle" at
     limit 8 returned the 6 paddles and then X-40 Balls. Ranking was right, the
     tail was noise. Raise this back toward 8-10 once the catalogue is large
     enough that the strong matches fill it on their own. */
  var LIMIT = 6;
  var timer = null, inflight = null, lastTerm = null;

  // The resting prompt is markup, not a JS string, so the copy stays editable in
  // header.liquid. Keep a copy to restore whenever the field is emptied.
  var prompt = results.innerHTML;

  /* Drop padded results.

     Predictive search is a typeahead, so it keeps filling toward the limit with
     progressively weaker matches rather than stopping when the good ones run
     out. "grips" came back with two paddles on the end; "overgrip" came back
     with the undergrip. The ORDER is always right — the real hits lead — but
     the tail is noise, and no parameter turns it off.

     The response carries no relevance score, so each row carries data-hay (see
     predictive-search.liquid) and we check it ourselves: a row survives if any
     query word, or its singular, appears somewhere in its searchable text.

     ⚠️ The fallback is the important half. If NOTHING matches literally, every
     row is kept and Shopify's fuzzy result stands untouched — that is what
     protects the typo path, which is worth more than the tidying. "spitfyre",
     "coralpro", "sixzero" and "ultre" all match nothing literally and all
     return the right product. Never let this filter empty the panel. */
  function singular(t) {
    if (t.length > 3 && t.slice(-2) === 'es') return t.slice(0, -2);
    if (t.length > 2 && t.slice(-1) === 's') return t.slice(0, -1);
    return t;
  }

  /* Strips diacritics so "ultre" matches "Ultré". Without this the literal test
     below failed on the best-selling product in the shop — nobody types the
     accent — the no-hit fallback kicked in, and the raw fuzzy result stood, which
     is how a search for "ultre" came back with the Ethos Undergrip next to the
     paddle. Applied to BOTH sides. Mirrored in snippets/search-prune.liquid,
     which has no normalize() and does it by substitution. */
  function fold(str) {
    return (str || '').toLowerCase().normalize ?
      (str || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') :
      (str || '').toLowerCase();
  }

  /* Brand names that are written closed-up get typed with spaces. "11 six 24"
     returned ZERO results for a shop whose best-selling vendor is "11six24":
     Shopify indexes that vendor as ONE token, so the query never reaches it, and
     prune() cannot help because prune only ever REMOVES rows. The answer has to
     come before the request, not after it. */
  function collapse(t) { return (t || '').replace(/\s+/g, ''); }

  function prune(term) {
    var rows = results.querySelectorAll('.srch__row');
    if (!rows.length) return;
    var words = fold(term).split(/[^a-z0-9]+/).filter(function (w) { return w.length > 1; });
    if (!words.length) return;
    var hits = [].filter.call(rows, function (row) {
      var hay = fold(row.getAttribute('data-hay'));
      return words.some(function (w) { return hay.indexOf(w) > -1 || hay.indexOf(singular(w)) > -1; });
    });
    // Nothing matched literally — a typo or a fuzzy stem. Leave Shopify's result alone.
    if (!hits.length || hits.length === rows.length) return;
    [].forEach.call(rows, function (row) { if (hits.indexOf(row) === -1) row.remove(); });
  }

  function render(html, term) {
    results.innerHTML = html;
    if (term) prune(term);
  }

  function query(term, retried) {
    // Abort the previous request rather than let a slow early keystroke land
    // after a fast later one and overwrite fresher results.
    if (inflight && inflight.abort) inflight.abort();
    var ctrl = (typeof AbortController === 'function') ? new AbortController() : null;
    inflight = ctrl;
    var url = root + 'search/suggest?q=' + encodeURIComponent(term) +
              '&section_id=predictive-search&resources[type]=product&resources[limit]=' + LIMIT +
              // Spelled out rather than left to the default, because the panel's copy
              // promises these: vendor = brand, title = model, product_type = category
              // (Paddle, Overgrip, Ball, Weight…), and shape from BOTH variants.title
              // and tag. Shape is a variant on models sold in several shapes and a
              // plain attribute on models that only come in one, so a tag is what
              // covers both — metafields are NOT indexed by predictive search, which
              // is why the spec metafields cannot serve this. "gen4" matches nothing
              // even though every paddle carries a Core row. Anything that has to be
              // searchable has to exist as a tag, a type, a vendor or a title.
              //
              // ⚠️ This list controls what is INCLUDED. It cannot be used to EXCLUDE.
              // Measured: a bogus field name returns zero results, so the parameter is
              // genuinely parsed — but `fields=title` on its own still matches vendors
              // ("spartus" returns Spitfire, whose title is just "Spitfire"), because
              // Shopify folds the vendor into the title index. Do not reach for this
              // expecting to narrow a field out; it will not.
              //
              // ⚠️ `tag` makes every tag searchable, including internal ones. Right now
              // that means "placeholder" returns the throwaway products; they go before
              // launch. Keep tags customer-facing from here on.
              '&resources[options][fields]=title,product_type,variants.title,vendor,tag';
    fetch(url, ctrl ? { signal: ctrl.signal } : undefined)
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        if (!html) return;
        render(html, term);
        /* Retry once with the whitespace collapsed, so "11 six 24" finds 11six24.
           It fires ONLY on a genuine miss, so it can never make a working query
           worse, and it is self-terminating by construction: the retry has no
           whitespace left to collapse, so there is no second retry to guard
           against. The results page runs the same rule — see the search-page
           fallback further down this file; change one and change the other. */
        if (retried) return;
        var alt = collapse(term);
        if (alt !== term && alt.length >= MIN_CHARS && !results.querySelector('.srch__row')) query(alt, true);
      })
      .catch(function () { /* aborted or offline — leave the last good results up */ });
  }

  function onType() {
    var term = input.value.trim();
    if (term === lastTerm) return;
    lastTerm = term;
    clearTimeout(timer);
    if (term.length < MIN_CHARS) { render(prompt); return; }
    timer = setTimeout(function () { query(term); }, DEBOUNCE);
  }

  input.addEventListener('input', onType);

  // Escape clears the field and drops focus, which closes the desktop panel
  // through :focus-within unless the pointer is still over it. In the sheet it
  // just empties the field, which is the right outcome there too.
  input.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    input.value = '';
    lastTerm = '';
    clearTimeout(timer);
    render(prompt);
    input.blur();
  });
  });
})();

/* Search results page — the same collapse fallback the header dropdown runs.

   A brand written closed-up ("11six24") typed with spaces ("11 six 24") is one
   token to Shopify's index, so the query returns nothing, and Liquid cannot read
   the query string to retry server-side. This only runs when the page actually
   rendered the empty state, and only commits if the retry found products, so a
   genuine no-result query is left exactly as it was.

   ⚠️ Deliberately NOT a server-side redirect, which was the first design. Liquid
   cannot tell a spaced brand from an ordinary miss, so bouncing every zero-result
   multi-word query would have rewritten "beginner paddle" to "beginnerpaddle" in
   the heading AND the URL — fixing one brand query by making every other empty
   state worse. Fetch, check, then swap.

   Mirrors the retry in query() above. Change one and change the other. */
(function () {
  var empty = document.querySelector('[data-search-empty]');
  if (!empty) return;

  var term = empty.getAttribute('data-term') || '';
  var alt = term.replace(/\s+/g, '');
  if (!alt || alt === term || alt.length < 2) return;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  // Same unavailable_products=show as every other route into /search — see
  // snippets/search-unavailable-input.liquid.
  var url = root + 'search?q=' + encodeURIComponent(alt) + '&options%5Bunavailable_products%5D=show';

  fetch(url)
    .then(function (r) { return r.ok ? r.text() : ''; })
    .then(function (html) {
      if (!html) return;
      var fetched = new DOMParser().parseFromString(html, 'text/html');
      var grid = fetched.querySelector('.grid');
      // .card--all is the "View All" tile, not a product — it must not count as a hit.
      if (!grid || !grid.querySelector('.card:not(.card--all)')) return;

      var host = empty.parentNode;
      if (!host) return;

      /* Say what happened. The field still holds what the shopper typed, so the
         grid must not silently present itself as the answer to that query. */
      var note = document.createElement('p');
      note.className = 'srch__corrected';
      note.textContent = 'Showing results for “' + alt + '”.';
      if (host.parentNode) host.parentNode.insertBefore(note, host);

      empty.remove();
      while (grid.firstChild) host.appendChild(grid.firstChild);

      /* The server-rendered <title> still says "0 results found" for the query that
         missed, which contradicts the results now on screen. Take the fetched page's
         own title rather than patching the string — it already carries the right
         count and the right query. */
      if (fetched.title) document.title = fetched.title;

      /* Leave a URL that works when reloaded or shared. replaceState, not
         pushState: the query that found nothing is not a page worth going back to. */
      if (window.history && history.replaceState) {
        try { history.replaceState({}, '', url); } catch (e) {}
      }
    })
    .catch(function () { /* offline or blocked — the empty state is already correct */ });
})();

/* Browser chrome tint. Chrome and Safari on iOS colour their toolbars from
   <meta name="theme-color">, which the theme hard-coded to #ffffff. Against the
   #0a0a0a footer that reads as a white bar pinned under the page — and because
   it is browser UI, nothing painted inside the document can cover it. The meta
   tag is honoured dynamically, so swap it as the footer enters the viewport.

   One value serves both toolbars, so top and bottom cannot disagree: the home
   hero is dark at the top while the body below it is white. The footer is the
   case that actually looked broken, so it wins. */
(function () {
  var meta = document.querySelector('meta[name="theme-color"]');
  var ftr = document.querySelector('.ftr');
  if (!meta || !ftr) return;

  var LIGHT = '#ffffff';
  var DARK = '#0a0a0a';
  var current = meta.getAttribute('content');

  function update() {
    // Has the footer reached the bottom edge of the viewport?
    var want = ftr.getBoundingClientRect().top < window.innerHeight ? DARK : LIGHT;
    if (want !== current) {
      current = want;
      meta.setAttribute('content', want);
    }
  }

  update();
  tpsnScroll.add(update);
})();


/* ─────────────────────────────────────────────────────────────────────────────
   Card hover parallax. The image zooms 10% and drifts toward the cursor; the
   CSS transition makes it TRAIL the pointer, which is what reads as parallax
   rather than as the picture being dragged.

   ⚠️ MAX pairs with the 1.10 scale in tpsn.css — see the note there before
   changing either. 10px of travel is inside the overflow the zoom creates, so
   the image can never pull away from the edge of its frame.

   Performance, following the rules this file has already paid for:
   · the card is measured ONCE on pointerenter, never per pointermove. Reading
     getBoundingClientRect on every move is the forced-layout-per-event mistake
     the scroll handlers were rewritten to remove.
   · writes are batched into one rAF tick, so a burst of moves in a frame
     produces a single style write.
   · only two custom properties are written. The transform itself lives in CSS
     and is composited; nothing here touches layout.
   · hover:hover + pointer:fine only. On a touch screen "hover" sticks after a
     tap, which would strand a card mid-zoom with no way to release it.
   ───────────────────────────────────────────────────────────────────────────── */
(function () {
  if (!window.matchMedia) return;
  if (!matchMedia('(hover:hover) and (pointer:fine)').matches) return;
  if (matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var MAX = 10;    /* px the photo drifts — paired with scale(1.1) in tpsn.css */
  var TILT = 5;    /* deg the tile rotates on each axis */
  var active = null, box = null, raf = null, u = 0, v = 0;

  function write() {
    raf = null;
    if (!active) return;
    /* The photo drifts WITH the cursor; the tile tilts AWAY from it — the corner
       under the pointer is pushed back, as if pressed. Those opposite signs are
       what separate the two layers in depth. Get them the same way round and the
       whole card just slides, which looks like a bug rather than an effect.
       Sign check: rotateX(+) pushes the TOP away, rotateY(+) pushes the RIGHT
       away, so the tilt is -v and +u respectively. */
    active.style.setProperty('--px', (u * MAX).toFixed(2) + 'px');
    active.style.setProperty('--py', (v * MAX).toFixed(2) + 'px');
    active.style.setProperty('--tx', (-v * TILT).toFixed(2) + 'deg');
    active.style.setProperty('--ty', (u * TILT).toFixed(2) + 'deg');
  }

  function release(card) {
    if (!card) return;
    card.style.removeProperty('--px');
    card.style.removeProperty('--py');
    card.style.removeProperty('--tx');
    card.style.removeProperty('--ty');
  }

  document.addEventListener('pointerover', function (e) {
    var card = e.target && e.target.closest ? e.target.closest('.card') : null;
    /* Only cards that actually carry a photograph. A placeholder tile has
       nothing to parallax and keeps the plain lift. */
    if (!card || !card.querySelector('.card__media img')) return;
    if (card === active) return;
    release(active);
    active = card;
    box = card.getBoundingClientRect();
  }, { passive: true });

  document.addEventListener('pointermove', function (e) {
    if (!active || !box) return;
    /* u and v are normalised to -1..1 from the card's centre, so both layers are
       driven off one measurement and cannot drift apart. */
    u = ((e.clientX - box.left) / box.width - 0.5) * 2;
    v = ((e.clientY - box.top) / box.height - 0.5) * 2;
    if (!raf) raf = requestAnimationFrame(write);
  }, { passive: true });

  document.addEventListener('pointerout', function (e) {
    if (!active) return;
    /* pointerout fires when moving between children too; only release when the
       pointer has genuinely left the card. */
    if (e.relatedTarget && active.contains(e.relatedTarget)) return;
    release(active);
    active = null;
    box = null;
  }, { passive: true });

  /* A scrolled page invalidates the cached rect. Cheaper to drop the hover than
     to re-measure on a scroll tick. */
  window.addEventListener('scroll', function () {
    if (!active) return;
    release(active);
    active = null;
    box = null;
  }, { passive: true });
})();

/* Option names on hover (Leroy, 2026-09-11). A circle carries a letter, a number
   or a colour, not a name, so on a fine pointer the full value floats just above
   the circle before anything is picked: "Hurache-X (Elongated)", "Lavender",
   "12 balls". A sold-out value says so. Keyboard focus shows it too.

   ONE element on <body>, position:fixed, rather than a child of the pill:
   .picks__inner clips its content for the 0fr -> 1fr group collapse, so a tip
   inside it would be cut off at the group's edge. The text is the radio's own
   value, so it can never disagree with what the button buys. Hidden on scroll,
   like the card tilt above, instead of re-measuring. */
(function () {
  if (!window.matchMedia) return;
  var fine = matchMedia('(hover:hover) and (pointer:fine)').matches;
  var tip = null, cur = null;

  function show(pill) {
    var input = pill.querySelector('input[data-option]');
    if (!input) return;
    if (!tip) {
      tip = document.createElement('div');
      tip.className = 'swtip';
      tip.setAttribute('aria-hidden', 'true');  /* the radio already has its name */
      document.body.appendChild(tip);
    }
    tip.textContent = input.value + (pill.classList.contains('pill--out') ? ' · Sold out' : '');
    var r = pill.getBoundingClientRect();
    tip.style.left = Math.round(r.left + r.width / 2) + 'px';
    tip.style.top = Math.round(r.top - 8) + 'px';
    tip.classList.add('is-on');
    cur = pill;
  }
  function hide() {
    if (tip) tip.classList.remove('is-on');
    cur = null;
  }

  if (fine) {
    document.addEventListener('pointerover', function (e) {
      var pill = e.target.closest ? e.target.closest('.pdp .pill--sw') : null;
      if (pill === cur) return;
      if (pill) show(pill); else if (cur) hide();
    }, { passive: true });
    document.documentElement.addEventListener('mouseleave', hide);
  }
  /* Keyboard: only a focus the browser would ring (:focus-visible), so a mouse
     click does not leave a tip hanging once the pointer has moved on. */
  document.addEventListener('focusin', function (e) {
    var pill = e.target.closest ? e.target.closest('.pdp .pill--sw') : null;
    if (pill && e.target.matches && e.target.matches(':focus-visible')) show(pill);
  });
  document.addEventListener('focusout', function (e) {
    if (cur && cur.contains(e.target)) hide();
  });
  window.addEventListener('scroll', function () { if (cur) hide(); }, { passive: true });
})();

/* ------------------------------------------- Who We Are — thermal wordmark ---
   The two photographs in the Who We Are band are replaced by a heat-diffusion
   field: the wordmark emits heat every frame, the field spreads and decays, and
   the cursor injects more along its path. Tuned in the standalone lab
   (reference/heat-lab.html) — the constants below are the settled values from
   that session, not defaults worth "tidying".

   Why WebGL2 and not canvas 2d: the field is a per-pixel simulation read back
   into itself every frame. On the CPU that is a full-canvas ImageData round trip
   per frame; on the GPU it is two textures and a 5-tap shader.

   ⚠️ The field covers the WHOLE section, including the area behind the copy, so
   the pointer is read off <section class="about">, not the canvas — the canvas
   itself is pointer-events:none and must stay that way or the paragraphs stop
   being selectable.

   ⚠️ Everything here is decorative. Any failure — no WebGL2, a lost context,
   prefers-reduced-motion — must leave the static wordmark (.about__still)
   visible, which is why data-heat-on is only ever set AFTER a frame has been
   drawn, and is removed again on context loss. */
(function () {
  var section = document.querySelector('.about[data-heat]');
  if (!section) return;
  var canvas = section.querySelector('.about__heat');
  var box = section.querySelector('[data-heat-box]');
  if (!canvas || !box || !canvas.getContext) return;

  /* Settled in the lab, 2026-09-14. EMIT/FORCE/AMBIENT are per-frame deposits:
     a stationary source settles at deposit / (1 - DECAY), so EMIT 0.0105 against
     DECAY 0.980 is a mark sitting at ~0.53 of full scale before exposure. Change
     DECAY and every one of the three changes brightness with it. */
  var EMIT = 0.0105,   /* heat the wordmark radiates per frame                */
      FORCE = 0.006,   /* heat the cursor deposits per frame while over the band */
      RADIUS = 0.255,  /* cursor brush radius, fraction of canvas width        */
      AMBIENT = 0.0055,/* the roaming source that keeps it alive without a cursor */
      SPEED = 0.21,    /* how fast that source roams                           */
      DIFF = 0.090,    /* diffusion per step — >0.25 makes the scheme explode  */
      DECAY = 0.980,   /* heat retained per frame                              */
      STEPS = 2,       /* simulation steps per frame                           */
      RES = 288,       /* simulation width in texels; height follows the aspect */
      GAIN = 1.14,     /* exposure                                             */
      LIFT = 0.31,     /* how much the mark's own body is lifted out of the field */
      GRAIN = 0.145,   /* film grain, the thing that stops it looking like CSS  */
      VIGNETTE = 0.08,
      MARK = 0.86,     /* wordmark width as a fraction of the old photo slot   */
      SCROLL_LIFT = 1.6,  /* a fast scroll multiplies the mark's own emission by up to this */
      SCROLL_AMB = 3.0,   /* and the roaming source by this — the flare is mostly here      */
      SCROLL_FULL = 60,   /* px of scroll per frame that counts as "full speed"             */
      DPR_CAP = 1.5,   /* the field is a soft gradient; more pixels buy nothing */
      WARMUP = 150;    /* steps run before the first paint, so it fades in settled */

  var RAMPS = { thermal: 0, mono: 1, steel: 2 };

  var VERT =
    '#version 300 es\n' +
    'void main(){vec2 p=vec2(float((gl_VertexID<<1)&2),float(gl_VertexID&2));' +
    'gl_Position=vec4(p*2.0-1.0,0.0,1.0);}';

  var SIM =
    '#version 300 es\nprecision highp float;\n' +
    'uniform sampler2D uPrev;uniform sampler2D uMask;' +
    'uniform vec2 uTexel,uAspect,uMaskScale,uMaskOffset,uP0,uP1,uAmbPos;' +
    'uniform float uDiff,uDecay,uEmit,uBrushR,uBrushF,uAmb,uPointer;' +
    'out vec4 outColor;' +
    'float maskAt(vec2 uv){vec2 m=(uv-uMaskOffset)/uMaskScale;' +
    'if(m.x<0.0||m.x>1.0||m.y<0.0||m.y>1.0)return 0.0;return texture(uMask,m).a;}' +
    'float segDist(vec2 p,vec2 a,vec2 b){vec2 pa=p-a,ba=b-a;' +
    'float h=clamp(dot(pa,ba)/max(dot(ba,ba),1e-6),0.0,1.0);return length((pa-ba*h)*uAspect);}' +
    'void main(){vec2 uv=gl_FragCoord.xy*uTexel;' +
    'float h=texture(uPrev,uv).r;' +
    'float l=texture(uPrev,uv-vec2(uTexel.x,0.0)).r;' +
    'float r=texture(uPrev,uv+vec2(uTexel.x,0.0)).r;' +
    'float d=texture(uPrev,uv-vec2(0.0,uTexel.y)).r;' +
    'float u=texture(uPrev,uv+vec2(0.0,uTexel.y)).r;' +
    'h+=uDiff*(l+r+u+d-4.0*h);h*=uDecay;h+=maskAt(uv)*uEmit;' +
    'float db=segDist(uv,uP0,uP1);h+=uBrushF*exp(-(db*db)/(uBrushR*uBrushR))*uPointer;' +
    'float da=length((uv-uAmbPos)*uAspect);float ar=uBrushR*1.4;' +
    'h+=uAmb*exp(-(da*da)/(ar*ar));' +
    'outColor=vec4(clamp(h,0.0,1.6),0.0,0.0,1.0);}';

  var DRAW =
    '#version 300 es\nprecision highp float;\n' +
    'uniform sampler2D uField;uniform sampler2D uMask;' +
    'uniform vec2 uTexel,uRes,uMaskScale,uMaskOffset;' +
    'uniform float uGain,uLift,uGrain,uVig,uTime,uRamp;' +
    'out vec4 outColor;' +
    'float maskAt(vec2 uv){vec2 m=(uv-uMaskOffset)/uMaskScale;' +
    'if(m.x<0.0||m.x>1.0||m.y<0.0||m.y>1.0)return 0.0;return texture(uMask,m).a;}' +
    'float hash(vec2 p){p=fract(p*vec2(443.897,441.423));p+=dot(p,p+19.19);' +
    'return fract((p.x+p.y)*p.x);}' +
    'vec3 thermal(float t){vec3 c=mix(vec3(0.008,0.010,0.024),vec3(0.043,0.118,0.494),smoothstep(0.00,0.30,t));' +
    'c=mix(c,vec3(0.086,0.510,0.855),smoothstep(0.26,0.50,t));' +
    'c=mix(c,vec3(0.247,0.816,0.847),smoothstep(0.48,0.64,t));' +
    'c=mix(c,vec3(0.941,0.627,0.165),smoothstep(0.62,0.78,t));' +
    'c=mix(c,vec3(0.957,0.318,0.165),smoothstep(0.76,0.90,t));' +
    'return mix(c,vec3(1.000,0.953,0.839),smoothstep(0.90,1.00,t));}' +
    'vec3 mono(float t){vec3 c=mix(vec3(0.000),vec3(0.086),smoothstep(0.00,0.34,t));' +
    'c=mix(c,vec3(0.302),smoothstep(0.32,0.58,t));' +
    'c=mix(c,vec3(0.639),smoothstep(0.56,0.80,t));' +
    'return mix(c,vec3(1.000),smoothstep(0.80,1.00,t));}' +
    'vec3 steel(float t){vec3 c=mix(vec3(0.016,0.027,0.043),vec3(0.063,0.125,0.180),smoothstep(0.00,0.34,t));' +
    'c=mix(c,vec3(0.173,0.298,0.400),smoothstep(0.32,0.58,t));' +
    'c=mix(c,vec3(0.490,0.608,0.702),smoothstep(0.56,0.80,t));' +
    'return mix(c,vec3(0.918,0.949,0.973),smoothstep(0.80,1.00,t));}' +
    'void main(){vec2 uv=gl_FragCoord.xy/uRes;' +
    'float h=texture(uField,uv).r;' +
    'h+=texture(uField,uv+vec2(uTexel.x,0.0)).r;h+=texture(uField,uv-vec2(uTexel.x,0.0)).r;' +
    'h+=texture(uField,uv+vec2(0.0,uTexel.y)).r;h+=texture(uField,uv-vec2(0.0,uTexel.y)).r;' +
    'h/=5.0;' +
    'float t=clamp(h*uGain+maskAt(uv)*uLift,0.0,1.0);' +
    't=t*(0.22+0.78*t);' +   /* darkens the low end so the band stays black */
    'vec3 col=uRamp<0.5?thermal(t):(uRamp<1.5?mono(t):steel(t));' +
    'col+=(hash(gl_FragCoord.xy+fract(uTime)*311.7)-0.5)*uGrain*smoothstep(0.02,0.35,t);' +
    'vec2 q=uv-0.5;col*=1.0-uVig*dot(q,q)*1.6;' +
    'outColor=vec4(max(col,0.0),1.0);}';

  var gl = canvas.getContext('webgl2', { antialias: false, alpha: false, depth: false, powerPreference: 'low-power' });
  if (!gl) return;                                   /* .about__still stays visible */

  function shader(src, type) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src); gl.compileShader(s);
    return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
  }
  function link(fs) {
    var v = shader(VERT, gl.VERTEX_SHADER), f = shader(fs, gl.FRAGMENT_SHADER);
    if (!v || !f) return null;
    var p = gl.createProgram();
    gl.attachShader(p, v); gl.attachShader(p, f); gl.linkProgram(p);
    return gl.getProgramParameter(p, gl.LINK_STATUS) ? p : null;
  }
  function uniforms(p) {
    var map = {}, n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS), i, info;
    for (i = 0; i < n; i++) { info = gl.getActiveUniform(p, i); map[info.name] = gl.getUniformLocation(p, info.name); }
    return map;
  }

  var simProg = link(SIM), drawProg = link(DRAW);
  if (!simProg || !drawProg) return;
  var simU = uniforms(simProg), drawU = uniforms(drawProg);
  var vao = gl.createVertexArray();
  /* Half-float keeps the dark halo smooth; RGBA8 bands it slightly but is a
     perfectly acceptable fallback, so a missing extension is not a bail-out. */
  var halfFloat = !!gl.getExtension('EXT_color_buffer_float');

  var ramp = RAMPS[section.getAttribute('data-heat-ramp')] || 0;
  var tex = [null, null], fb = [gl.createFramebuffer(), gl.createFramebuffer()];
  var simW = 0, simH = 0, idx = 0, aspect = 1, maskTex = null, maskAspect = 1;
  var reduced = window.matchMedia && matchMedia('(prefers-reduced-motion:reduce)').matches;
  var running = false, visible = false, started = false;
  var ptr = { x: 0.5, y: 0.5, px: 0.5, py: 0.5, on: 0, target: 0 };
  var rect = null, boxRect = null, phase = Math.random() * 100, last = 0;
  /* Scrolling feeds the field. It is what gives the band a reason to move on a
     phone, where there is no cursor at all: arriving at the section flares the
     wordmark, stopping lets it settle. Rise is fast and fall is slow (12 vs 3),
     because heat that faded as quickly as it arrived would read as a flicker. */
  var scrollBoost = 0, lastScrollY = window.pageYOffset;

  function measure() {
    rect = canvas.getBoundingClientRect();
    boxRect = box.getBoundingClientRect();
    aspect = rect.width / Math.max(rect.height, 1);
  }

  function sizeCanvas() {
    var dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
    var w = Math.max(1, Math.round(rect.width * dpr)), h = Math.max(1, Math.round(rect.height * dpr));
    if (w !== canvas.width || h !== canvas.height) { canvas.width = w; canvas.height = h; return true; }
    return false;
  }

  function allocate() {
    simW = Math.max(32, RES);
    simH = Math.max(32, Math.round(RES / Math.max(aspect, 0.2)));
    for (var i = 0; i < 2; i++) {
      if (tex[i]) gl.deleteTexture(tex[i]);
      var t = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, t);
      if (halfFloat) gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA16F, simW, simH, 0, gl.RGBA, gl.HALF_FLOAT, null);
      else gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, simW, simH, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      tex[i] = t;
      gl.bindFramebuffer(gl.FRAMEBUFFER, fb[i]);
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, t, 0);
      gl.clearColor(0, 0, 0, 1); gl.clear(gl.COLOR_BUFFER_BIT);
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  /* Where the wordmark sits, in canvas UV with y up: centred on the box that
     holds the old photo slot's geometry, at MARK of its width, keeping the
     artwork's own 1849:480 ratio. Recomputed every frame from cached rects, so
     it follows the grid through any reflow without a resize listener. */
  function markTransform() {
    var w = MARK * boxRect.width / rect.width;
    var h = w * (rect.width / rect.height) / maskAspect;
    var cx = (boxRect.left + boxRect.width / 2 - rect.left) / rect.width;
    var cy = 1 - (boxRect.top + boxRect.height / 2 - rect.top) / rect.height;
    return { sx: w, sy: h, ox: cx - w / 2, oy: cy - h / 2 };
  }

  function step(dt, time) {
    var mt = markTransform();
    ptr.on += (ptr.target - ptr.on) * Math.min(1, dt * 8);

    gl.bindVertexArray(vao);
    gl.useProgram(simProg);
    gl.viewport(0, 0, simW, simH);
    gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, maskTex);
    gl.uniform1i(simU.uMask, 1);
    gl.uniform2f(simU.uTexel, 1 / simW, 1 / simH);
    gl.uniform2f(simU.uAspect, aspect, 1);
    gl.uniform2f(simU.uMaskScale, mt.sx, mt.sy);
    gl.uniform2f(simU.uMaskOffset, mt.ox, mt.oy);
    gl.uniform1f(simU.uDiff, DIFF);
    gl.uniform1f(simU.uDecay, Math.pow(DECAY, 1 / STEPS));
    gl.uniform1f(simU.uEmit, EMIT * (1 + SCROLL_LIFT * scrollBoost) / STEPS);
    gl.uniform1f(simU.uBrushR, RADIUS);
    gl.uniform1f(simU.uBrushF, FORCE / STEPS);
    gl.uniform1f(simU.uAmb, AMBIENT * (1 + SCROLL_AMB * scrollBoost) / STEPS);
    gl.uniform1f(simU.uPointer, ptr.on);
    /* The roaming source is centred on the mark and reaches about a third of the
       band either way, so it animates the wordmark first and only leaks toward
       the copy. Without a cursor this IS the effect — every phone sees only this. */
    var a = time * SPEED + phase;
    gl.uniform2f(simU.uAmbPos,
      mt.ox + mt.sx / 2 + 0.30 * Math.sin(a),
      mt.oy + mt.sy / 2 + 0.22 * Math.sin(a * 0.73 + 1.3));

    for (var i = 0; i < STEPS; i++) {
      var f = i / STEPS, g = (i + 1) / STEPS;
      gl.uniform2f(simU.uP0, ptr.px + (ptr.x - ptr.px) * f, ptr.py + (ptr.y - ptr.py) * f);
      gl.uniform2f(simU.uP1, ptr.px + (ptr.x - ptr.px) * g, ptr.py + (ptr.y - ptr.py) * g);
      gl.bindFramebuffer(gl.FRAMEBUFFER, fb[1 - idx]);
      gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, tex[idx]);
      gl.uniform1i(simU.uPrev, 0);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      idx = 1 - idx;
    }
    ptr.px = ptr.x; ptr.py = ptr.y;

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.useProgram(drawProg);
    gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, tex[idx]);
    gl.uniform1i(drawU.uField, 0);
    gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, maskTex);
    gl.uniform1i(drawU.uMask, 1);
    gl.uniform2f(drawU.uTexel, 1 / simW, 1 / simH);
    gl.uniform2f(drawU.uRes, canvas.width, canvas.height);
    gl.uniform2f(drawU.uMaskScale, mt.sx, mt.sy);
    gl.uniform2f(drawU.uMaskOffset, mt.ox, mt.oy);
    gl.uniform1f(drawU.uGain, GAIN);
    gl.uniform1f(drawU.uLift, LIFT);
    gl.uniform1f(drawU.uGrain, GRAIN);
    gl.uniform1f(drawU.uVig, VIGNETTE);
    gl.uniform1f(drawU.uTime, time);
    gl.uniform1f(drawU.uRamp, ramp);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  function frame(now) {
    if (!running) return;
    requestAnimationFrame(frame);
    if (!visible || document.hidden) return;
    var dt = Math.min((now - last) / 1000, 0.05);
    last = now;
    var y = window.pageYOffset;
    var target = Math.min(1, Math.abs(y - lastScrollY) / SCROLL_FULL);
    lastScrollY = y;
    scrollBoost += (target - scrollBoost) * Math.min(1, dt * (target > scrollBoost ? 12 : 3));
    measure();
    if (sizeCanvas()) allocate();
    step(dt, now / 1000);
  }

  /* The pointer is read off the section so the trail follows the cursor over the
     copy too. y is flipped because gl_FragCoord counts up from the bottom. */
  function track(e) {
    if (!rect) return;
    ptr.x = (e.clientX - rect.left) / rect.width;
    ptr.y = 1 - (e.clientY - rect.top) / rect.height;
    ptr.target = 1;
  }
  section.addEventListener('pointermove', track, { passive: true });
  section.addEventListener('pointerdown', track, { passive: true });
  section.addEventListener('pointerleave', function () { ptr.target = 0; }, { passive: true });

  canvas.addEventListener('webglcontextlost', function (e) {
    e.preventDefault();
    running = false;
    section.removeAttribute('data-heat-on');   /* back to the static wordmark */
  });

  function start() {
    measure();
    sizeCanvas();
    allocate();
    /* Run the sim forward before the first paint: the field fades in already
       settled instead of blooming out of black while the reader watches. */
    for (var i = 0; i < WARMUP; i++) step(1 / 60, i / 60);
    section.setAttribute('data-heat-on', '');
    started = true;
    if (reduced) return;                        /* one settled frame, no loop */
    running = true;
    last = performance.now();
    requestAnimationFrame(frame);
  }

  var img = new Image();
  img.crossOrigin = 'anonymous';                /* the mask comes off the Shopify CDN */
  img.onload = function () {
    maskAspect = img.width / img.height;
    maskTex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, maskTex);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    /* ⚠️ The mask is the artwork's ALPHA, and maskAt() samples .a for that reason.
       tpsn-logo.png is BLACK ink on transparent: its RGB is 0 across the whole
       file, ink and padding alike, so a LUMINANCE upload (or any .r read) returns
       an empty mask and the wordmark emits no heat at all — the band then shows
       only the roaming ambient source. That shipped once, on 2026-09-14. */
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    /* Nothing runs until the band is actually on screen, and the loop stops
       again when it leaves — this is a per-frame GPU pass on a page that is
       mostly read, not watched. */
    if (!window.IntersectionObserver) { visible = true; start(); return; }
    /* The rAF chain, once started, is never torn down — it early-returns while
       the band is off screen. Restarting it on re-entry instead would leave two
       chains running for every scroll past the section. */
    new IntersectionObserver(function (entries) {
      visible = entries[0].isIntersecting;
      if (visible && !started) start();
    }, { rootMargin: '200px 0px' }).observe(section);
  };
  img.src = canvas.getAttribute('data-mask');
})();

/* ------------------------------------------------------------ scroll reveal ---
   A 14px rise and a fade, once per element, as it enters. The hidden state is
   applied by tpsn.css under .js-motion — see the SELECTOR LIST there, which this
   file has to match.

   ⚠️ COUPLED: REVEAL_SELECTOR below is the same list as the one in tpsn.css.
   CSS hides, this un-hides. An element in the CSS list but not this one stays
   invisible forever, which is the one failure this system can produce — so when
   either list changes, change both. */
(function () {
  if (!document.documentElement.classList.contains('js-motion')) return;   /* reduced motion */

  /* ⚠️ .ftr__rows removed 2026-09-18 (see tpsn.css): it could sit invisible, and
     the footer's socials and policy links are not allowed to be optional. Do not
     re-add it here without re-adding it there. */
  var REVEAL_SELECTOR = '.eyebrow,.section-label,.about__cols>div:last-child>*,' +
    '.sub__cols>div>*,.ftr__mail,.ftr__ask>*,body.template--index .grid>*';
  var els = document.querySelectorAll(REVEAL_SELECTOR);
  var i;

  function show(el) { el.classList.add('is-in'); }

  /* No observer support: reveal everything immediately rather than leave the page
     half empty. The animation is the optional part, the content is not. */
  if (!window.IntersectionObserver) {
    for (i = 0; i < els.length; i++) show(els[i]);
    return;
  }

  /* -12% at the bottom means an element starts its rise once it is properly in
     the viewport rather than the instant its first pixel appears — the difference
     between "things animate as you scroll" and "things are already finished by
     the time you look at them". Anything above the fold at load still fires on
     the observer's first callback, so the first screen settles immediately. */
  var io = new IntersectionObserver(function (entries) {
    for (var n = 0; n < entries.length; n++) {
      if (!entries[n].isIntersecting) continue;
      show(entries[n].target);
      io.unobserve(entries[n].target);        /* once, never on the way back up */
    }
  }, { rootMargin: '0px 0px -12% 0px', threshold: 0.01 });

  /* Split at the fold: what is already on screen belongs to the page-entrance
     cascade and is shown instantly (no transition, see .is-instant in tpsn.css);
     only what is below it gets an observer. One layout read per element, once,
     at boot — and it removes the need to guess at a timing window. */
  var pending = [];
  for (i = 0; i < els.length; i++) {
    if (els[i].getBoundingClientRect().top < window.innerHeight) {
      els[i].className += ' is-in is-instant';
    } else {
      io.observe(els[i]);
      pending.push(els[i]);
    }
  }

  /* Safety net for the one failure this system can produce (2026-09-16: Leroy
     found .ftr__rows — the footer's socials and policy links — stuck invisible
     on a product page while .ftr__mail and .ftr__ask, observed the same way,
     had revealed).

     The root cause was NOT reproducible: the element is in both selector lists,
     tpsn.js is deferred so the footer exists when the observer is built, and at
     full scroll the geometry clears the threshold with 73px of a 118px element
     inside the root. An automation tab cannot settle it either, since neither
     rAF nor IntersectionObserver delivery runs in a background tab. So this
     does not pretend to fix a diagnosed bug — it removes the possibility of the
     symptom, which is content the customer never sees.

     ⚠ It uses the SAME line the observer uses — innerHeight minus the 12%
     rootMargin — so it reveals at exactly the moment the observer would and
     changes no timing. Whichever fires first wins; the other finds it already
     in `pending`-free state. Drop the element from `pending` on reveal so this
     shrinks to a no-op, and never lower the 0.88 without lowering rootMargin
     too or things will start popping in early. */
  if (pending.length) {
    tpsnScroll.add(function () {
      for (var k = pending.length - 1; k >= 0; k--) {
        var el = pending[k];
        if (el.className.indexOf('is-in') > -1) { pending.splice(k, 1); continue; }
        var r = el.getBoundingClientRect();
        if (r.bottom > 0 && r.top < window.innerHeight * 0.88) {
          show(el);
          io.unobserve(el);
          pending.splice(k, 1);
        }
      }
    });
  }
})();

/* --------------------------------------------------- hero video, once covered ---
   The hero is pinned for the length of the document (see "the wipe" in
   tpsn.css), so the footage would otherwise keep decoding behind an opaque page
   for the whole visit. Pause it the moment it is fully covered and start it again
   on the way back up.

   play() returns a promise that rejects if the browser declines (an autoplay
   policy, or a pause arriving mid-play); it is muted and inline so it should not,
   but an unhandled rejection would surface as a console error. */
(function () {
  var hero = document.querySelector('.hero--video');
  var video = hero && hero.querySelector('.hero__video');
  if (!video) return;

  /* Same trap as syncHeader above: the hero is pinned, so nothing on the hero
     itself says how far the page has moved. "Covered" is simply the wiping
     section having reached the top of the viewport. */
  var wiper = hero.parentNode && hero.parentNode.nextElementSibling;
  var covered = false;

  tpsnScroll.add(function () {
    var now = wiper ? wiper.getBoundingClientRect().top <= 0
                    : window.pageYOffset >= hero.offsetHeight;
    if (now === covered) return;
    covered = now;
    if (covered) video.pause();
    else { var p = video.play(); if (p && p.catch) p.catch(function () {}); }
  });
})();


/* ── Product entrance: take the guard off once it has played ──────────────────
   .pdp ships with .is-entering in the server-rendered markup, which is what lets
   the diagonal wavefront in tpsn.css exist at first paint with no flash of
   already-visible content. This removes it afterwards, and the reason is
   renderSpecs(): it rebuilds every <dt> and <dd> on a variant change, and brand
   new nodes re-run their CSS animations. Leave the class on and the whole spec
   table fades itself back in on every swatch tap, underneath the variant
   crossfade that is already animating it.

   900ms clears the slowest element (about 320ms of delay plus a 320ms run) with
   room to spare. Removing the class cannot hide anything: these animations have
   fill-mode backwards, not forwards, so an element's resting state is its normal
   one and dropping the rule mid-flight would show it, never hide it.

   If a shopper changes variant inside that first 900ms the table animates once
   more. Worth knowing, not worth a state machine. */
(function () {
  var pdp = document.querySelector('.pdp.is-entering');
  if (!pdp) return;
  /* 1200ms, not 900: the phone schedule runs longer than the desktop one (the
     last spec row starts at ~582ms and takes 320ms to play), and dropping the
     guard mid-flight would strip the animation off whatever is still running. */
  setTimeout(function () { pdp.classList.remove('is-entering'); }, 1200);
})();

/* ---------------------------------------------------------------------------
   Page-to-page photo morph: only when the photo is actually on screen
   (2026-09-23). `.pdp .gallery` carries a STATIC view-transition-name on
   desktop (tpsn.css, tpsn-pdp-media) so one product's photograph morphs into
   the next. But the old snapshot is taken wherever the gallery sits at that
   moment -- and a shopper who scrolled down to "You may also like" and clicked
   a card has the gallery far above the viewport. The group then animated from
   there to the top of the new page, so the new photo slid DOWN into place
   instead of fading in (Leroy: "the photo scrolls down and not a fade in").

   Same rule as the same-document variant swap above: no name once the gallery
   is under the nav. Removing it on the OLD page only, in pageswap (which fires
   before the old snapshot), leaves a group that exists on the new side alone,
   and that plays as the plain fade-in. At the top of a product page -- the case
   the morph was built for -- nothing changes. */
(function () {
  window.addEventListener('pageswap', function (e) {
    if (!e.viewTransition) return;
    var gallery = document.querySelector('.pdp .gallery');
    if (!gallery) return;
    var navEl = document.querySelector('[data-header]');
    var navBottom = navEl ? navEl.getBoundingClientRect().bottom : 0;
    if (gallery.getBoundingClientRect().top < navBottom) {
      gallery.style.viewTransitionName = 'none';
    }
  });
  /* Back button: a bfcache-restored page comes back with the inline 'none'
     still on it, which would switch the morph off for the next navigation
     even from the top of the page. Hand the name back to the stylesheet. */
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    var gallery = document.querySelector('.pdp .gallery');
    if (gallery) gallery.style.viewTransitionName = '';
  });
})();

/* ---------------------------------------------------------------------------
   Phone buy bar: fixed to the screen until the product grid ends
   (2026-09-23). The CSS and the full reasoning are in tpsn.css at
   ".bar-live" — in short, a bottom-STICKY bar made Chrome for iPhone repaint
   the page in bands whenever its toolbar moved. This swaps sticky for fixed
   and hands the bar back to its grid slot when the grid's end is on screen.
   IntersectionObserver, not a scroll listener (house rule). The marker is a
   1px element placed right after .pdp__grid, so its top IS the grid's end:
   on screen or above it means rested. Desktop never matches the CSS, so the
   classes are inert there. */
(function () {
  var pdp = document.querySelector('section.pdp');
  var grid = pdp && pdp.querySelector('.pdp__grid');
  if (!grid || !('IntersectionObserver' in window)) return;
  var mark = document.createElement('div');
  mark.setAttribute('aria-hidden', 'true');
  mark.style.cssText = 'height:1px;margin-top:-1px;pointer-events:none';
  grid.insertAdjacentElement('afterend', mark);
  new IntersectionObserver(function (entries) {
    var e = entries[entries.length - 1];
    pdp.classList.toggle('bar-rested', e.isIntersecting || e.boundingClientRect.top < 0);
    pdp.classList.add('bar-live');
  }).observe(mark);
})();
